/**
 * Act 2 Store — 第二幕：二人世界
 *
 * 管理第二幕的轮次、双维度指标、LLM生成卡牌、话题阶段、消息等状态。
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { CharacterMetrics } from '@shared/types/character';
import type { Act2Card, TopicStage } from '@shared/types/cards';
import type { ChatMessage, Act1Result, SenderRole } from '@shared/types/gameState';
import {
  INITIAL_AFFECTION,
  INITIAL_COMFORT,
  INITIAL_ENERGY,
  INITIAL_COMMITMENT,
  ACT2_HAND_SIZE,
  BARELY_PASS_COMFORT_PENALTY,
} from '@shared/constants/gameBalance';

type Act2Phase = 'SCENE_SELECT' | 'WAITING' | 'PLAYER_TURN' | 'PROCESSING' | 'FINISHED';

interface Act2StoreState {
  round: number;
  phase: Act2Phase;
  messages: ChatMessage[];

  // 场景
  scene: string;

  // 话题阶段
  topicStage: TopicStage;
  usedTopics: string[];

  // 双维度指标
  metricsA: CharacterMetrics;
  metricsB: CharacterMetrics;

  // 卡牌系统
  deck: Act2Card[];
  hand: Act2Card[];
  swapCount: number;

  // 第一幕惩罚
  comfortPenalty: number;
  cardEffectReduction: number;

  // 瞬态 UI
  isTyping: boolean;
  typingSpeaker: string | null;

  // 流式对话状态
  currentSpeaker: string | null;
  streamingText: string;
  streamingAgentName: string | null;
  streamingAgentRole: SenderRole | null;

  // 卡牌叙事流式状态
  cardNarrativeText: string;
  isCardNarrating: boolean;

  // 上下文摘要缓存
  historySummary: string | null;
  lastSummarizedIndex: number;
}

interface Act2StoreActions {
  initialize: (act1Result?: Act1Result) => void;

  // 场景
  setScene: (sceneId: string) => void;

  // 轮次
  nextRound: () => void;
  setPhase: (phase: Act2Phase) => void;

  // 话题
  advanceTopicStage: () => void;
  addUsedTopic: (topic: string) => void;

  // 消息
  appendMessage: (msg: ChatMessage) => void;
  appendMessages: (msgs: ChatMessage[]) => void;

  // 指标
  updateMetrics: (side: 'A' | 'B', changes: Partial<CharacterMetrics>) => void;

  // 卡牌
  setDeck: (cards: Act2Card[]) => void;
  addCardsToDeck: (cards: Act2Card[]) => void;
  playCard: (cardId: string) => Act2Card | undefined;
  drawToHand: (count: number) => void;
  incrementSwapCount: () => void;

  // UI
  setIsTyping: (v: boolean) => void;
  setTypingSpeaker: (id: string | null) => void;

  // 流式对话
  setCurrentSpeaker: (id: string | null) => void;
  appendStreamingText: (text: string) => void;
  clearStreaming: () => void;
  commitStreamingMessage: () => void;
  setStreamingAgent: (name: string | null, role: SenderRole | null) => void;

  // 卡牌叙事
  setIsCardNarrating: (v: boolean) => void;
  appendCardNarrative: (text: string) => void;
  commitCardNarrative: (cardName: string, fullText: string) => void;
  clearCardNarrative: () => void;

  // 摘要
  setHistorySummary: (summary: string, lastIndex: number) => void;

  // 重置
  reset: () => void;
}

export type Act2Store = Act2StoreState & Act2StoreActions;

const TOPIC_ORDER: TopicStage[] = ['破冰', '兴趣', '情感', '价值观', '未来'];

function makeInitialMetrics(comfortPenalty: number): CharacterMetrics {
  return {
    affection: INITIAL_AFFECTION,
    comfort: Math.max(0, INITIAL_COMFORT - comfortPenalty),
    energy: INITIAL_ENERGY,
    commitment: INITIAL_COMMITMENT,
  };
}

const INITIAL_STATE: Act2StoreState = {
  round: 1,
  phase: 'SCENE_SELECT',
  messages: [],
  scene: '',
  topicStage: '破冰',
  usedTopics: [],
  metricsA: makeInitialMetrics(0),
  metricsB: makeInitialMetrics(0),
  deck: [],
  hand: [],
  swapCount: 0,
  comfortPenalty: 0,
  cardEffectReduction: 0,
  isTyping: false,
  typingSpeaker: null,
  currentSpeaker: null,
  streamingText: '',
  streamingAgentName: null,
  streamingAgentRole: null,
  cardNarrativeText: '',
  isCardNarrating: false,
  historySummary: null,
  lastSummarizedIndex: 0,
};

function clampMetrics(m: CharacterMetrics): CharacterMetrics {
  return {
    affection: Math.max(0, Math.min(100, m.affection)),
    comfort: Math.max(0, Math.min(100, m.comfort)),
    energy: Math.max(0, Math.min(100, m.energy)),
    commitment: Math.max(0, Math.min(100, m.commitment)),
  };
}

export const useAct2Store = create<Act2Store>()(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,

      initialize: (act1Result) => {
        const penalty = act1Result?.comfortPenalty ?? 0;
        const reduction = act1Result?.cardEffectReduction ?? 0;

        // 地下情模式：舒适度额外惩罚
        const totalComfortPenalty = act1Result?.undergroundLove
          ? penalty + BARELY_PASS_COMFORT_PENALTY
          : penalty;

        set({
          ...INITIAL_STATE,
          comfortPenalty: totalComfortPenalty,
          cardEffectReduction: reduction,
          metricsA: makeInitialMetrics(totalComfortPenalty),
          metricsB: makeInitialMetrics(totalComfortPenalty),
        });
      },

      setScene: (sceneId) => set({ scene: sceneId, phase: 'WAITING' }),

      nextRound: () => set(s => ({ round: s.round + 1 })),
      setPhase: (phase) => set({ phase }),

      advanceTopicStage: () => set(s => {
        const idx = TOPIC_ORDER.indexOf(s.topicStage);
        if (idx < TOPIC_ORDER.length - 1) {
          return { topicStage: TOPIC_ORDER[idx + 1] };
        }
        return {};
      }),

      addUsedTopic: (topic) => set(s => ({
        usedTopics: [...s.usedTopics, topic],
      })),

      appendMessage: (msg) => set(s => ({ messages: [...s.messages, msg] })),
      appendMessages: (msgs) => set(s => ({ messages: [...s.messages, ...msgs] })),

      updateMetrics: (side, changes) => set(s => {
        const key = side === 'A' ? 'metricsA' : 'metricsB';
        const current = s[key];
        const updated: CharacterMetrics = {
          affection: current.affection + (changes.affection ?? 0),
          comfort: current.comfort + (changes.comfort ?? 0),
          energy: current.energy + (changes.energy ?? 0),
          commitment: current.commitment + (changes.commitment ?? 0),
        };
        return { [key]: clampMetrics(updated) };
      }),

      setDeck: (cards) => set({ deck: cards }),

      addCardsToDeck: (cards) => set(s => ({
        deck: [...s.deck, ...cards],
      })),

      playCard: (cardId) => {
        const state = get();
        const card = state.hand.find(c => c.id === cardId);
        if (!card) return undefined;
        set({ hand: state.hand.filter(c => c.id !== cardId) });
        return card;
      },

      drawToHand: (count) => set(s => {
        const needed = Math.min(count, ACT2_HAND_SIZE - s.hand.length, s.deck.length);
        if (needed <= 0) return s;
        const drawn = s.deck.slice(0, needed);
        return {
          hand: [...s.hand, ...drawn],
          deck: s.deck.slice(needed),
        };
      }),

      incrementSwapCount: () => set(s => ({ swapCount: s.swapCount + 1 })),

      setIsTyping: (v) => set({ isTyping: v }),
      setTypingSpeaker: (id) => set({ typingSpeaker: id }),

      // 流式对话
      setCurrentSpeaker: (id) => set({ currentSpeaker: id }),
      appendStreamingText: (text) => set(s => ({ streamingText: s.streamingText + text })),
      clearStreaming: () => set({ streamingText: '', streamingAgentName: null, streamingAgentRole: null }),
      setStreamingAgent: (name, role) => set({ streamingAgentName: name, streamingAgentRole: role }),
      commitStreamingMessage: () => set(s => {
        if (!s.streamingText || !s.currentSpeaker) return s;
        const msg: ChatMessage = {
          id: `act2_stream_${Date.now()}_${s.currentSpeaker}`,
          senderId: s.currentSpeaker,
          senderName: s.streamingAgentName || '未知',
          senderRole: s.streamingAgentRole || 'system',
          text: s.streamingText,
          type: 'text',
        };
        return {
          messages: [...s.messages, msg],
          streamingText: '',
          streamingAgentName: null,
          streamingAgentRole: null,
        };
      }),

      // 卡牌叙事
      setIsCardNarrating: (v) => set({ isCardNarrating: v }),
      appendCardNarrative: (text) => set(s => ({ cardNarrativeText: s.cardNarrativeText + text })),
      commitCardNarrative: (cardName, fullText) => set(s => {
        const msg: ChatMessage = {
          id: `act2_narrative_${Date.now()}`,
          senderId: 'system',
          senderName: cardName,
          senderRole: 'system',
          text: fullText,
          type: 'narrative',
        };
        return {
          messages: [...s.messages, msg],
          cardNarrativeText: '',
          isCardNarrating: false,
        };
      }),
      clearCardNarrative: () => set({ cardNarrativeText: '', isCardNarrating: false }),

      // 摘要
      setHistorySummary: (summary, lastIndex) => set({ historySummary: summary, lastSummarizedIndex: lastIndex }),

      reset: () => set({ ...INITIAL_STATE }),
    }),
    {
      name: 'cm-v2-act2',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        round: state.round,
        phase: state.phase,
        messages: state.messages,
        scene: state.scene,
        topicStage: state.topicStage,
        usedTopics: state.usedTopics,
        metricsA: state.metricsA,
        metricsB: state.metricsB,
        deck: state.deck,
        hand: state.hand,
        swapCount: state.swapCount,
        comfortPenalty: state.comfortPenalty,
        cardEffectReduction: state.cardEffectReduction,
        historySummary: state.historySummary,
        lastSummarizedIndex: state.lastSummarizedIndex,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        state.isTyping = false;
        state.typingSpeaker = null;
        state.currentSpeaker = null;
        state.streamingText = '';
        state.streamingAgentName = null;
        state.streamingAgentRole = null;
        state.cardNarrativeText = '';
        state.isCardNarrating = false;
      },
    },
  ),
);
